import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef, useState, useId, useCallback, createElement } from "react";
import styled from "styled-components";
import { TextButton, IconButton } from "@creditornot/cb-button";
import { colors, cbSemanticCssVars, typographyCss } from "@creditornot/cb-ingredients";
import { createCssVars, pxToRem, createCssVarDefinitions, createAttributeCssVarDefinitions, assignInlineCssVars } from "@creditornot/cb-utils";
const AlertCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M11.984.003a12.205 12.205 0 00-8.51 3.652A11.799 11.799 0 00.002 12.21 11.778 11.778 0 0011.8 24h.214c6.677-.069 12.039-5.53 11.985-12.208A11.765 11.765 0 0011.984.003zM10.5 16.543a1.476 1.476 0 011.449-1.53h.027c.82.002 1.492.65 1.522 1.47a1.475 1.475 0 01-1.448 1.53h-.027a1.529 1.529 0 01-1.523-1.47zm.5-4.041v-6a1 1 0 012 0v6a1 1 0 01-2 0z" }) }));
const AlertCircleFilledIcon = AlertCircleFilled;
const CheckmarkCircleFilled = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx(
    "path",
    {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M0 12C0 5.373 5.373 0 12 0c6.624.008 11.992 5.376 12 12 0 6.627-5.373 12-12 12S0 18.627 0 12zm17.552-5.78a1.25 1.25 0 011.197 1.977l-6.7 9.087a1.263 1.263 0 01-1.787.235l-4.783-3.826a1.251 1.251 0 011.563-1.953l3.767 3.014 5.924-8.04c.196-.267.49-.445.819-.495z"
    }
  ) })
);
const CheckmarkCircleFilledIcon = CheckmarkCircleFilled;
const SvgCross = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M2.44 19.44a1.5 1.5 0 002.121 2.121l7.262-7.26a.25.25 0 01.354 0l7.262 7.262a1.5 1.5 0 102.122-2.12l-7.26-7.266a.25.25 0 010-.354l7.262-7.262a1.5 1.5 0 00-2.124-2.12L12.177 9.7a.25.25 0 01-.354 0L4.561 2.44A1.5 1.5 0 002.44 4.56L9.7 11.823a.25.25 0 010 .354L2.44 19.44z" }) }));
const CrossIcon = SvgCross;
const InfoCircleFilled = forwardRef((props, ref) => /* @__PURE__ */ jsx("svg", { viewBox: "0 0 24 24", ...props, ref, children: /* @__PURE__ */ jsx("path", { d: "M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.993 5.376 18.624.007 12 0zm.25 5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm2.25 13.5h-4a1 1 0 010-2h.75a.25.25 0 00.25-.25v-4.5a.25.25 0 00-.25-.25h-.75a1 1 0 010-2h1a2 2 0 012 2v4.75c0 .138.112.25.25.25h.75a1 1 0 010 2z" }) }));
const InfoCircleFilledIcon = InfoCircleFilled;
const styleParamsCssVars = createCssVars(["padding", "fontSize"]);
const internalCssVars = createCssVars(["lineHeight", "iconSize", "closeButtonSize"]);
const sizeConfigs = {
  small: {
    padding: "8px",
    fontSize: pxToRem(12)
  },
  medium: {
    padding: "16px",
    fontSize: pxToRem(14)
  }
};
const variantConfigs = {
  error: {
    bg: {
      light: colors.strawberry8,
      dark: colors.strawberry16
    },
    iconFill: {
      light: colors.strawberry100,
      dark: colors.strawberry100
    }
  },
  info: {
    bg: {
      light: colors.pepper8,
      dark: colors.salt12
    },
    iconFill: {
      light: colors.pepper100,
      dark: colors.salt100
    }
  },
  success: {
    bg: {
      light: colors.lime8,
      dark: colors.lime16
    },
    iconFill: {
      light: colors.lime100,
      dark: colors.lime100
    }
  },
  warning: {
    bg: {
      light: colors.banana8,
      dark: colors.banana16
    },
    iconFill: {
      light: colors.banana100,
      dark: colors.banana100
    }
  }
};
const variantCssVars = createCssVars(["bg", "iconFill"]);
const icons = {
  error: AlertCircleFilledIcon,
  info: InfoCircleFilledIcon,
  success: CheckmarkCircleFilledIcon,
  warning: AlertCircleFilledIcon
};
const Root = styled.div`
  ${createCssVarDefinitions({
  lineHeight: `calc(${styleParamsCssVars.fontSize} / 14 * 20)`,
  iconSize: `min(calc(${styleParamsCssVars.fontSize} / 14 * 16), calc(${styleParamsCssVars.padding} / 8 * 12))`,
  closeButtonSize: `max(24px, calc(var(--lineHeight) * 1.2))`
})}

  ${createAttributeCssVarDefinitions(
  "data-variant",
  {
    options: Object.keys(variantConfigs),
    varContract: { bg: null, iconFill: null }
  },
  variantConfigs
)}

  box-sizing: border-box;
  min-width: 100px;
  max-width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
  border-radius: 8px;
  flex-shrink: 0;
  color: ${cbSemanticCssVars.textPrimary};
  background-color: ${variantCssVars.bg};
  padding: ${styleParamsCssVars.padding};
  ${typographyCss.baseBodyStyle()}
  line-height: var(--lineHeight);
  font-size: ${styleParamsCssVars.fontSize};
`;
const Col = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;
const LeftCol = styled(Col)`
  flex-shrink: 0;
  padding-inline-end: ${styleParamsCssVars.padding};
`;
const RightCol = styled(Col)`
  flex: 1;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;
const IconContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: ${internalCssVars.lineHeight};

  > svg {
    display: block;
    width: ${internalCssVars.iconSize};
    fill: ${variantCssVars.iconFill};
  }
`;
const TitleRow = styled.div`
  display: flex;
  box-sizing: border-box;
  max-width: 100%;
`;
const TitleContainer = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
`;
const Title = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;
  white-space: nowrap;
`;
const TitleContent = styled.div``;
const TitleColumn = styled.div`
  flex-shrink: 0;
  padding-inline-start: ${styleParamsCssVars.padding};
`;
const CloseButtonContainer = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: ${internalCssVars.lineHeight};
`;
const Content = styled.div`
  margin-top: max(4px, calc(${styleParamsCssVars.fontSize} / 14 * 4));
`;
const Collapse = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 400ms cubic-bezier(0.19, 1, 0.22, 1); /* easeOutExpo */

  &[aria-hidden='false'] {
    grid-template-rows: 1fr;
  }
`;
const ShowMoreContent = styled.div`
  overflow: hidden;
`;
const ShowMoreInner = styled.div`
  padding-top: max(4px, calc(${styleParamsCssVars.fontSize} / 14 * 4));
`;
const Alert = ({
  children,
  closeButtonProps,
  initiallyOpen = false,
  onCloseButtonClick,
  showMoreContent,
  size = "medium",
  style,
  styleParams,
  title,
  titleRightContent,
  variant = "info",
  ...rest
}) => {
  const [open, setOpen] = useState(initiallyOpen);
  const collapsedContentId = useId();
  const handleShowMoreButtonClick = useCallback(() => setOpen((value) => !value), []);
  return /* @__PURE__ */ jsxs(
    Root,
    {
      "data-variant": variant,
      role: "alert",
      style: {
        ...style,
        ...assignInlineCssVars(styleParamsCssVars, {
          ...sizeConfigs[size],
          ...styleParams
        })
      },
      ...rest,
      children: [
        /* @__PURE__ */ jsx(LeftCol, { children: /* @__PURE__ */ jsx(IconContainer, { children: createElement(icons[variant]) }) }),
        /* @__PURE__ */ jsxs(RightCol, { children: [
          /* @__PURE__ */ jsxs(TitleRow, { children: [
            /* @__PURE__ */ jsx(TitleContainer, { children: title ? /* @__PURE__ */ jsx(Title, { title, children: title }) : /* @__PURE__ */ jsx(TitleContent, { children }) }),
            !!titleRightContent && /* @__PURE__ */ jsx(TitleColumn, { children: titleRightContent }),
            !!showMoreContent && /* @__PURE__ */ jsx(TitleColumn, { children: /* @__PURE__ */ jsx(
              TextButton,
              {
                "aria-controls": collapsedContentId,
                "aria-expanded": open,
                onClick: handleShowMoreButtonClick,
                size: "inherit",
                variant: "grey",
                children: open ? "Less" : "More"
              }
            ) }),
            !!onCloseButtonClick && /* @__PURE__ */ jsx(TitleColumn, { children: /* @__PURE__ */ jsx(CloseButtonContainer, { children: /* @__PURE__ */ jsx(
              IconButton,
              {
                ...closeButtonProps,
                alignment: "iconEdges",
                "aria-label": "Close alert",
                background: "animated",
                cssVarOverrides: { size: internalCssVars.closeButtonSize },
                onClick: onCloseButtonClick,
                size: "tiny",
                variant: "grey",
                children: /* @__PURE__ */ jsx(CrossIcon, {})
              }
            ) }) })
          ] }),
          !!children && !!title && /* @__PURE__ */ jsx(Content, { children }),
          !!showMoreContent && /* @__PURE__ */ jsx(Collapse, { "aria-hidden": !open, id: collapsedContentId, children: /* @__PURE__ */ jsx(ShowMoreContent, { children: /* @__PURE__ */ jsx(ShowMoreInner, { children: showMoreContent }) }) })
        ] })
      ]
    }
  );
};
export {
  Alert
};
//# sourceMappingURL=index.js.map
